import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ApartmentForm,
  ApartmentFormValues,
} from "../../components/apartment-form";
import { Modal } from "../../components/modal";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetApartments } from "../../queries/get-apartments";
import { parseFormValuesToAdditionalFields } from "../../services/additional-fields";
import { createApartment } from "../../services/apartment";
import { navigateBack } from "../../utils/navigate-back";
import { stringToFloat, stringToInt } from "../../utils/string-to-number";

export function ApartmentCreate() {
  const navigate = useNavigate();
  const { data: apartments } = useGetApartments();
  const { data: additionalFields } = useAdditionalFields("apartment");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const highestIndex = useMemo(() => {
    return (
      apartments?.reduce(
        (max, apartment) => Math.max(max, apartment.index ?? 0),
        0,
      ) ?? 0
    );
  }, [apartments]);

  const [formValues, setFormValues] = useState<ApartmentFormValues>({
    index: "",
    objectNumber: "",
    streetNumber: "",
    apartmentNumber: "",
    floorNumber: "",
    area: "",
    share: "",
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  useEffect(() => {
    if (!highestIndex) {
      return;
    }
    setFormValues((currentValue) => ({
      ...currentValue,
      index: `${highestIndex + 1}`,
    }));
  }, [highestIndex]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/apartment");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <ApartmentForm
        hasError={hasError}
        loading={loading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await createApartment({
                index: stringToInt(formValues.index),
                ...(formValues.objectNumber.trim() !== "" && {
                  objectNumber: formValues.objectNumber,
                }),
                ...(formValues.streetNumber.trim() !== "" && {
                  streetNumber: formValues.streetNumber,
                }),
                ...(formValues.apartmentNumber.trim() !== "" && {
                  apartmentNumber: formValues.apartmentNumber,
                }),
                ...(formValues.floorNumber.trim() !== "" && {
                  floorNumber: formValues.floorNumber,
                }),
                ...(formValues.area.trim() !== "" && {
                  area: stringToFloat(formValues.area),
                }),
                ...(formValues.share.trim() !== "" && {
                  share: stringToFloat(formValues.share),
                }),
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                  ),
                }),
              });
              await queryClient.invalidateQueries({ queryKey: ["apartments"] });
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
